import { devtoolsExchange } from "@urql/devtools";
import { authExchange } from "@urql/exchange-auth";
// TODO: use normalized cache once hasura includes remote schema merging (See: https://github.com/hasura/graphql-engine/issues/5801)
// import { offlineExchange } from "@urql/exchange-graphcache";
import { retryExchange } from "@urql/exchange-retry";
import type { JwtPayload } from "jwt-decode";
import jwtDecode from "jwt-decode";
// import type { IntrospectionQuery } from "graphql";
import * as React from "react";
// import { SubscriptionClient } from "subscriptions-transport-ws";
import type { Client } from "urql";
import {
  createClient,
  debugExchange,
  fetchExchange,
  gql,
  makeOperation,
  Provider,
  // subscriptionExchange,
} from "urql";
import type { Cache } from "~/cache/graphcache/src";
import { offlineExchange } from "~/cache/graphcache/src";
import { useAuth0 } from "~/config/auth0/use-auth0";
import { env } from "~/config/env";
import type { CreateTaskInput } from "~/gql/graphql";
import {
  ProjectTasksList_DataDocument,
  TaskEdit_DataDocument,
} from "~/gql/graphql";
import { FullPageSpinner } from "~/screens/App/components/FullPageSpinner/FullPageSpinner";
import type { ProjectTasksList_DataQuery } from "~/screens/App/screens/Project/Tasks/screens/ProjectTasksListScreen/components/ProjectTasksList/ProjectTasksList.graphql";
import type { TaskItem } from "~/screens/App/screens/Project/Tasks/screens/ProjectTasksListScreen/components/ProjectTasksList/ProjectTasksList.types";
import { currentNetworkOnlineStatus } from "../capacitor/network/hook";
import {
  generateTemporaryTaskId,
  getStoredVariables,
  handleCreateTaskOptimisticUpdate,
} from "./helpers";
import { updateTaskValidationInCache } from "./offlineValidation.helpers";
import { makeIndexedDbStorage, makeLocalStorage } from "./storages";

interface UrqlConfigProps {
  children: React.ReactNode;
}

// Enable debug mode to debug urql cache
const debug = false; //!!env.DEV;

const getStorage = () => {
  return makeIndexedDbStorage();

  if (env.DEV) {
    return makeLocalStorage();
  }

  return makeIndexedDbStorage();
};

export const extractQueryFromErrorMessage = (
  str: string
): string | undefined => {
  if (str.includes("The GraphQL query at the field at")) {
    // Use a regular expression to match the content between backticks
    const match = str.match(/`(.*?)`/);
    const query = match?.[1];
    return query;
  } else if (str.includes('No value for field "id" on entity')) {
    const match = str.match(/entity "(.*)"/);
    const query = match?.[1];
    return query;
  }

  return;
};

const isRootQueryProblem = (query: string): boolean => {
  let numberOfDotsToMatch = 1;
  // if end with digits it's an array so, we can have  one more dots
  if (/\d$/.test(query)) {
    numberOfDotsToMatch++;
  }

  const numberOfDots = query.split(".").length - 1;
  return numberOfDots === numberOfDotsToMatch;
};

const invalidateCache = (cache: Cache, types: string[] = []) => {
  types.forEach((type) => {
    cache.invalidate(type);
  });
};

const offlineCache = offlineExchange({
  debug,
  /*
  isOfflineError(error, _result) {
    // by default
    However, this is optional, and the default function checks for common offline error messages and checks 
    navigator.onLine 
    only available if we put a full offline mode but for testing, we only block graphql request
    
    console.log("🔥urql:cache:isOfflineError", { _result, error });
    return !!error?.networkError;
  },
  */

  // We can't cache aggregate queries because they don't have a key
  // To avoid error to be display, we should add below
  keys: {
    /**
     * Bad remote schema
     */
    GetOrgProejectTemplate: () => null,

    // should be composed index
    orgs_to_users: () => null,

    project_roles: () => null,

    /**
     * 
     * 
    // no index key
    */
    projects_count: () => null,

    projects_map_overview_aggregate: () => null,

    projects_to_users: () => null,

    tasks_aggregate: () => null,

    // why no id?
    user_avatar: () => null,
    user_avatars: () => null,
  },
  // If we have some cache error, cache could be corrupted and return null
  // So we use console.error to log the error
  // https://github.com/urql-graphql/urql/issues/3526
  // Our custom logger prevent developper if there is no ID at the root query
  logger: (severity, message) => {
    const query = extractQueryFromErrorMessage(message);

    const isQuery = (str: string) =>
      str.startsWith("Query") || str.startsWith("query_root");
    if (query) {
      if (isQuery(query) && isRootQueryProblem(query)) {
        console.error(query, {
          causedBy: message.split("Caused At")?.[1],
          message,
          query,
          type: "urql:cache:root",
        });
      } else {
        if (debug) {
          console.warn(query, {
            message,
            query,
            type: "urql:cache:children",
          });
        }
      }
    } else {
      /*
      console.info(message, {
        message,
        type: "urql:cache:other",
      });
      */
    }
  },

  optimistic: {
    createTask: () => {
      const taskId = generateTemporaryTaskId(true);

      return {
        __typename: "CreateTaskSuccess",
        task_id: taskId,
      };
    },

    deleteTask: (args) => {
      const argsObj = { ...args } as { input: { id: string } };
      const taskId = argsObj.input.id;

      return {
        __typename: "DeleteTaskSuccess",
        task_id: taskId,
      };
    },

    updateSubtask: (args, cache, info) => {
      const argsObj = { ...args } as { input: { subtaskId: string } };
      const subtaskId = argsObj.input.subtaskId;

      return {
        __typename: "UpdateSubtaskSuccess",
        subtask_id: subtaskId,
      };
    },

    updateTask: (args, cache, info) => {
      const argsObj = { ...args } as { input: { id: string } };
      const taskId = argsObj.input.id;

      return {
        __typename: "UpdateTaskSuccess",
        task_id: taskId,
      };
    },

    updateUserProfile: (args, cache, info) => {
      // Is called 2 times, once for optimistic update and once for the real update
      console.log("🔥 updateUserProfile optimstic mutation update", {
        args,
        cache,
        info,
      });

      const variables = info.variables;
      // coming from the mutation arguments
      const userId = variables.userId;

      return {
        __typename: "UpdateUserProfileSuccess",
        // not stateless, so we should get the userId from somewhere
        user_id: userId,
      };
    },

    updateValidationTask: (args, _cache, _info) => {
      const argsObj = { ...args } as { input: { taskId: string } };
      const taskId = argsObj.input.taskId;

      return {
        __typename: "UpdateValidationTaskSuccess",
        task_id: taskId,
      };
    },
  },

  // If we use schema, updates Mutation seemns not to be called,
  // so we can't invalidate the cache
  // schema: schema,
  storage: getStorage(),

  updates: {
    Mutation: {
      // We customized graphcache for having typenames
      "*": (_result, _args, cache, info) => {
        const context = info.context;
        console.log("🔥🔥🔥urql:updates:Mutation:*", {
          _args,
          _result,
          cache,
          info,
        });
        const additionalTypenames = context?.additionalTypenames;
        if (additionalTypenames?.length) {
          // If we do not invalidate the ui won't take changes
          if (!info.optimistic) {
            invalidateCache(cache, additionalTypenames);
          }
        }
      },

      createTask: (_result, args, cache, info) => {
        if (info.optimistic) {
          const { input } = { ...args } as { input: CreateTaskInput };
          handleCreateTaskOptimisticUpdate(input, cache);
        }
      },

      deleteTask: (_result, _args, cache, info) => {
        if (info.optimistic) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const taskId = (_result?.deleteTask as any).task_id as string;

          const variables = getStoredVariables();

          cache.updateQuery(
            {
              query: ProjectTasksList_DataDocument,
              variables: variables,
            },
            (data) => {
              if (!data || !data.projects_by_pk || !data.projects_by_pk.tasks) {
                return data;
              }

              const filteredTasks = data.projects_by_pk.tasks.map((task) => {
                if ((task as TaskItem).id === taskId) {
                  return {
                    ...task,
                    deleted: true,
                  };
                }
                return task;
              });

              const updatedData = {
                ...data,
                projects_by_pk: {
                  ...data.projects_by_pk,
                  tasks: filteredTasks,
                },
              };

              return updatedData;
            }
          );
        }
      },

      updateSubtask: (_result, _args, cache, info) => {
        if (info.optimistic) {
          const subtaskId = (_result?.updateSubtask as { subtask_id: string })
            .subtask_id;
          const input = _args.input as {
            closed: boolean;
            description: string;
            endDate: string | null;
            startDate: string | null;
            subtaskId: string;
          };

          const variables = getStoredVariables();

          const projectId = variables.projectId;

          const queries = cache.inspectFields("Query");

          const taskQueries = queries.filter((query) => {
            const isTaskQuery =
              query.fieldName === "tasks" ||
              query.fieldName === "tasks_by_pk" ||
              query.fieldName === "projects_by_pk";

            return isTaskQuery;
          });

          for (const query of taskQueries) {
            const queryArgs = query.arguments ?? {};

            if (!projectId) {
              console.log(
                "🔥 updateSubtask: Skipping tasks query - no projectId.",
                {
                  fieldKey: query.fieldKey,
                  queryArgs,
                }
              );
              continue;
            }

            // Update tasks query
            if (query.fieldName === "tasks") {
              cache.updateQuery(
                {
                  query: ProjectTasksList_DataDocument,
                  //@ts-expect-error – todo: fix this
                  variables: { ...queryArgs, projectId },
                },
                (data: ProjectTasksList_DataQuery) => {
                  if (!data || typeof data !== "object" || !("tasks" in data)) {
                    console.log(
                      "🔥 updateSubtask: No data found for tasks query",
                      {
                        fieldKey: query.fieldKey,
                        queryArgs,
                      }
                    );
                    return data;
                  }

                  const typedData = data as {
                    tasks: Array<{ subtasks?: Array<{ id: string }> }>;
                  };
                  if (!typedData.tasks) {
                    console.log("🔥 updateSubtask: No tasks found in data", {
                      data,
                      fieldKey: query.fieldKey,
                      queryArgs,
                    });
                    return data;
                  }

                  const updatedTasks = typedData.tasks.map((task) => {
                    if (!task.subtasks) return task;

                    const updatedSubtasks = task.subtasks.map((subtask) => {
                      if (subtask.id === subtaskId) {
                        return {
                          ...subtask,
                          closed: input.closed,
                          description: input.description,
                          end_date: input.endDate,
                          start_date: input.startDate,
                        };
                      }
                      return subtask;
                    });

                    return {
                      ...task,
                      subtasks: updatedSubtasks,
                    };
                  });

                  return {
                    ...data,
                    tasks: updatedTasks,
                  };
                }
              );
            }

            if (query.fieldName === "projects_by_pk") {
              cache.updateQuery(
                {
                  query: ProjectTasksList_DataDocument,
                  //@ts-expect-error – todo: fix this
                  variables: { ...queryArgs, projectId },
                },
                (data: unknown) => {
                  if (
                    !data ||
                    typeof data !== "object" ||
                    !("projects_by_pk" in data)
                  ) {
                    console.log(
                      "🔥 updateSubtask: No data found for projects_by_pk query",
                      {
                        fieldKey: query.fieldKey,
                        queryArgs,
                      }
                    );
                    return data;
                  }

                  const typedData = data as {
                    projects_by_pk: {
                      tasks?: Array<{ subtasks?: Array<{ id: string }> }>;
                    };
                  };
                  if (!typedData.projects_by_pk?.tasks) {
                    console.log(
                      "🔥 updateSubtask: No tasks found in projects_by_pk data",
                      {
                        data,
                        fieldKey: query.fieldKey,
                        queryArgs,
                      }
                    );
                    return data;
                  }

                  const updatedTasks = typedData.projects_by_pk.tasks.map(
                    (task) => {
                      if (!task.subtasks) return task;

                      const updatedSubtasks = task.subtasks.map((subtask) => {
                        if (subtask.id === subtaskId) {
                          return {
                            ...subtask,
                            closed: input.closed,
                            description: input.description,
                            end_date: input.endDate,
                            start_date: input.startDate,
                          };
                        }
                        return subtask;
                      });

                      return {
                        ...task,
                        subtasks: updatedSubtasks,
                      };
                    }
                  );

                  return {
                    ...data,
                    projects_by_pk: {
                      ...typedData.projects_by_pk,
                      tasks: updatedTasks,
                    },
                  };
                }
              );
            }

            if (query.fieldName === "tasks_by_pk") {
              const taskId = (query.arguments as { id: string })?.id;
              if (!taskId) continue;

              cache.updateQuery(
                {
                  query: TaskEdit_DataDocument,
                  variables: { projectId, taskId },
                },
                (data: unknown) => {
                  if (!data || typeof data !== "object" || !("task" in data)) {
                    console.log(
                      "🔥 updateSubtask: No data found for tasks_by_pk query",
                      {
                        fieldKey: query.fieldKey,
                        queryArgs,
                      }
                    );
                    return data;
                  }

                  const typedData = data as {
                    task: { subtasks?: Array<{ id: string }> };
                  };
                  if (!typedData.task) {
                    console.log("🔥 updateSubtask: No task found in data", {
                      data,
                      fieldKey: query.fieldKey,
                      queryArgs,
                    });
                    return data;
                  }

                  if (!typedData.task.subtasks) {
                    console.log("🔥 updateSubtask: No subtasks found in task", {
                      data,
                      fieldKey: query.fieldKey,
                      queryArgs,
                    });
                    return data;
                  }

                  const updatedSubtasks = typedData.task.subtasks.map(
                    (subtask) => {
                      if (subtask.id === subtaskId) {
                        return {
                          ...subtask,
                          closed: input.closed,
                          description: input.description,
                          end_date: input.endDate,
                          start_date: input.startDate,
                        };
                      }
                      return subtask;
                    }
                  );

                  return {
                    ...data,
                    task: {
                      ...typedData.task,
                      subtasks: updatedSubtasks,
                    },
                  };
                }
              );
            }
          }
        }
      },

      updateTask: (_result, _args, cache, info) => {
        console.log("🔥 task updated.");

        const taskId = (_result?.updateTask as { task_id: string }).task_id;
        const input = _args.input as {
          id: string;
          start_date?: string | null;
          end_date?: string | null;
          description?: string;
          [key: string]: any;
        };

        // Check if this is a date update (Gantt drag & drop)
        const isDateUpdate =
          input &&
          (input.start_date !== undefined || input.end_date !== undefined);

        if (info.optimistic) {
          const variables = getStoredVariables();

          cache.updateQuery(
            {
              query: ProjectTasksList_DataDocument,
              variables: variables,
            },
            (data) => {
              if (!data || !data.projects_by_pk || !data.projects_by_pk.tasks) {
                return data;
              }

              const updatedTasks = data.projects_by_pk.tasks.map((task) => {
                if ((task as TaskItem).id === taskId) {
                  return { ...task, ...(input as object) };
                }
                return task;
              });

              return {
                ...data,
                projects_by_pk: {
                  ...data.projects_by_pk,
                  tasks: updatedTasks,
                },
              };
            }
          );
        }

        // For date updates on real server response, also invalidate cache
        if (isDateUpdate && !info.optimistic) {
          invalidateCache(cache, ["tasks", "projects_by_pk"]);
        }
      },

      updateUserProfile: (_result, _args, cache, info) => {
        console.log("🔥 updateUserProfile mutation update", {
          _args,
          _result,
          cache,
          info,
        });

        if (info.optimistic) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const userId = (_result as any)?.updateUserProfile?.user_id;
          if (!userId) {
            return;
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const input: any = _args.input;

          const fragment = gql`
            fragment _ on users {
              id
              country_code
              first_name
              language
              last_name
              phone
            }
          `;

          cache.writeFragment(fragment, {
            ...input,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            id: userId,
          });
        }
      },

      updateValidationTask: (_result, _args, cache, info) => {
        if (info.optimistic) {
          const taskId = (_result?.updateValidationTask as { task_id: string })
            ?.task_id;

          const variables = getStoredVariables();
          const projectId = variables.projectId;

          updateTaskValidationInCache(cache, taskId, projectId);
        }
      },
    },
  },
});

function useClient() {
  const { getAccessTokenSilently } = useAuth0();

  // TODO: Fix hasura subscription high cpu usage before re-enabling websocket
  // See:
  // https://github.com/hasura/graphql-engine/issues/6542
  // https://github.com/hasura/graphql-engine/issues/6042
  //
  // const subscriptionClient = new SubscriptionClient(
  //   env.VITE_HASURA_SUBSCRIPTION_URL,
  //   {
  //     async connectionParams() {
  //       const token = await getAccessTokenSilently();
  //       return {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       };
  //     },
  //     // See: https://github.com/hasura/graphql-engine/issues/4509#issuecomment-882503283
  //     inactivityTimeout: 30000,
  //     lazy: true,
  //     minTimeout: 2000,
  //     reconnect: true,
  //   }
  // );

  const client = createClient({
    exchanges: [
      ...(debug ? [devtoolsExchange, debugExchange] : []),
      offlineCache,

      retryExchange({
        initialDelayMs: 1000,
        maxDelayMs: 15000,
        maxNumberAttempts: 2,
        randomDelay: true,
        retryIf: (err) => {
          const isOffline = !currentNetworkOnlineStatus;

          // only retry if the error is a network error and the network is online
          // when offline, retry is trigger by the auth token refresh
          return !!err?.networkError && !isOffline;
        },
      }),
      authExchange(async (utils) => {
        let token = null;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          console.log("error", e);
        }

        const authState = {
          token,
        };

        return {
          addAuthToOperation(operation) {
            // the token isn't in the auth state, return the operation without changes
            if (!authState?.token) {
              console.log("no token");
              return operation;
            }

            // fetchOptions can be a function (See Client API) but you can simplify this based of usage
            const fetchOptions =
              typeof operation.context.fetchOptions === "function"
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions ?? {};

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: `Bearer ${authState.token}`,
                },
              },
            });
          },

          didAuthError(error) {
            // check if the error was an auth error (this can be implemented in various ways, e.g. 401 or a special error code)
            return error.graphQLErrors.some(
              (e) => e.extensions?.code === "FORBIDDEN"
            );
          },
          // Should be optional, but it's not...
          refreshAuth() {
            return Promise.resolve();
          },
          willAuthError: (operation) => {
            const authToken = authState?.token;
            if (!authToken) {
              return true;
            }
            // Check token formating and expirancy
            try {
              const decodedToken = jwtDecode<JwtPayload>(authToken);
              if (decodedToken) {
                const expiry = decodedToken.exp ?? 0;
                // Check if the token is still valid for at least 10 seconds
                const now = (Date.now() + 10000) / 1000;
                const isExpired = Boolean(expiry < now);
                // Token is already expired, so we need to refresh it before performing next request
                if (isExpired) {
                  return true;
                }
              }
            } catch (e) {
              // JWT decode failed, so the token is invalid
              return true;
            }
            // No error, so we can continue with the request and shouldn't have auth error
            return false;
          },
        };
      }),
      fetchExchange,
      // subscriptionExchange({
      //   forwardSubscription(operation) {
      //     return subscriptionClient.request(operation);
      //   },
      // }),
    ],
    requestPolicy: "cache-and-network",
    url: env.VITE_HASURA_URL,
  });

  return client;
}

export function UrqlConfig(props: UrqlConfigProps) {
  const client = useClient();

  if (debug) {
    debugEvent(client);
  }

  if (client === null) {
    return <FullPageSpinner />;
  }

  return <Provider value={client}>{props.children}</Provider>;
}

export const debugEvent = (client: Client) => {
  client?.subscribeToDebugTarget?.((event) => {
    console.log("urql:debug", event);
  });
};
